import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Crown, Send, ArrowLeft, Circle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TierBadge from "../components/TierBadge";

const POLL_MS = 3000;
const ONLINE_THRESHOLD_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isOnline(lastSeen) {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < ONLINE_THRESHOLD_MS;
}

function PremiumGate() {
  const navigate = useNavigate();
  return (
    <div className="grid place-items-center py-20" data-testid="messages-paywall">
      <div className="max-w-md rounded-3xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-8 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-500 text-white"><Crown size={22}/></div>
        <h2 className="mt-4 font-heading text-2xl font-semibold">Messaging is Premium</h2>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Upgrade to Premium to unlock messaging.</p>
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="messages-paywall-pricing">₹49/month · ₹299/year</p>
        <Button onClick={() => navigate("/premium")} className="mt-6 bg-gradient-to-r from-amber-400 to-orange-500 text-white" data-testid="messages-upsell-btn">Upgrade to Premium</Button>
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const { friendId } = useParams();
  if (!effectivePremium) return <PremiumGate />;
  return <MessagesInner currentUser={user} activeFriendId={friendId} />;
}

// How close to the bottom (in px) counts as "at the bottom". Reads like
// WhatsApp — a bit of headroom so a tiny scroll-up doesn't disable auto-scroll.
const NEAR_BOTTOM_PX = 120;

function MessagesInner({ currentUser, activeFriendId }) {
  const [convos, setConvos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [friend, setFriend] = useState(null);
  const [draft, setDraft] = useState("");
  const navigate = useNavigate();
  // The scroll container ref — we drive scrollTop on this directly instead
  // of scrollIntoView() on a sentinel, so the auto-scroll never bubbles up
  // into an ancestor scroller.
  const listRef = useRef(null);
  // "Was the user near the bottom before this render?" — the sole gate on
  // whether we auto-scroll when messages update. Starts true so the first
  // render (initial load) lands at the bottom.
  const isNearBottomRef = useRef(true);
  const prevFriendIdRef = useRef(null);
  const prevLastMessageIdRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const { data } = await api.get("/messages/conversations");
    setConvos(data);
  }, []);

  const loadThread = useCallback(async (fid) => {
    const { data } = await api.get(`/messages/with/${fid}`);
    setMessages(data.messages);
    setFriend(data.friend);
  }, []);

  // Hard delete the current chat. Idempotent on the backend; on success we
  // wipe the local view and navigate back to the conversation list.
  const deleteChat = useCallback(async () => {
    if (!activeFriendId) return;
    try {
      await api.delete(`/messages/conversation/${activeFriendId}`);
      setMessages([]);
      // Drop the conversation from the sidebar list immediately so the user
      // doesn't see a "ghost" entry until the next poll.
      setConvos((prev) => prev.filter((c) => c.user_id !== activeFriendId));
      toast.success("Chat deleted");
      navigate("/messages");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete chat. Try again.");
    }
  }, [activeFriendId, navigate]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  // Presence heartbeat
  useEffect(() => {
    api.post("/presence/ping").catch((e) => {
      if (e?.response?.status !== 401 && process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("presence ping failed:", e?.message);
      }
    });
    const id = setInterval(() => {
      api.post("/presence/ping").catch(() => { /* periodic noise — ignore */ });
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // Poll active thread + convos
  useEffect(() => {
    if (!activeFriendId) return;
    loadThread(activeFriendId);
    const id = setInterval(() => {
      loadThread(activeFriendId);
      loadConvos();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [activeFriendId, loadThread, loadConvos]);

  // Reset scroll tracking when the user switches threads. A brand new
  // thread should always land at the bottom regardless of where they were
  // in the previous chat.
  useEffect(() => {
    if (activeFriendId !== prevFriendIdRef.current) {
      prevFriendIdRef.current = activeFriendId;
      isNearBottomRef.current = true;
      prevLastMessageIdRef.current = null;
    }
  }, [activeFriendId]);

  // Track whether the user is currently near the bottom. Used as the sole
  // gate on the auto-scroll effect below. Note: this fires ONLY on human
  // scroll gestures — programmatic scrollTop changes below don't retrigger.
  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = dist < NEAR_BOTTOM_PX;
  }, []);

  // Auto-scroll ONLY when:
  //   (a) the user was already near the bottom before this render, AND
  //   (b) a genuinely new message arrived (new last message_id).
  // Rule (b) means the 3s polling loop that keeps returning the same
  // messages won't yank the view — only real new messages do.
  useEffect(() => {
    const el = listRef.current;
    if (!el || messages.length === 0) return;
    const lastId = messages[messages.length - 1].message_id;
    const isNew = lastId !== prevLastMessageIdRef.current;
    prevLastMessageIdRef.current = lastId;
    if (!isNew) return;
    if (!isNearBottomRef.current) return;
    // Use instant on first render (initial load), smooth thereafter.
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const content = draft.trim();
    if (!content || !activeFriendId) return;
    setDraft("");
    // Sending my own message always jumps to the bottom (WhatsApp behaviour):
    // force the near-bottom flag so the auto-scroll effect fires this render.
    isNearBottomRef.current = true;
    const tmp = {
      message_id: `tmp_${Date.now()}`,
      from_user_id: currentUser.user_id,
      to_user_id: activeFriendId,
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tmp]);
    try {
      await api.post("/messages/send", { to_user_id: activeFriendId, content });
      loadThread(activeFriendId);
      loadConvos();
    } catch {
      setMessages((m) => m.filter((x) => x.message_id !== tmp.message_id));
    }
  };

  return (
    <div className="grid h-[calc(100vh-8rem)] grid-cols-1 overflow-hidden rounded-2xl border border-zinc-200 bg-white md:grid-cols-[320px_1fr] dark:border-zinc-800 dark:bg-zinc-900" data-testid="messages-root">
      {/* Sidebar */}
      <aside className={`flex flex-col border-r border-zinc-200 dark:border-zinc-800 ${activeFriendId ? "hidden md:flex" : "flex"}`}>
        <div className="flex items-center gap-2 border-b border-zinc-200 p-4 dark:border-zinc-800">
          <button
            className="-ml-1 rounded-md p-1 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
            onClick={() => navigate("/community")}
            aria-label="Back to Community"
            data-testid="messages-back-to-community-btn"
          >
            <ArrowLeft size={18}/>
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-lg font-semibold">Messages</h2>
            <p className="text-xs text-zinc-500">{convos.length} friend{convos.length === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {convos.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">No friends yet. Add some on the Community tab.</div>
          ) : convos.map((c) => {
            const online = isOnline(c.last_seen);
            const active = c.user_id === activeFriendId;
            return (
              <button
                key={c.user_id}
                onClick={() => navigate(`/messages/${c.user_id}`)}
                data-testid={`convo-${c.user_id}`}
                className={`flex w-full items-center gap-3 border-b border-zinc-100 px-4 py-3 text-left transition dark:border-zinc-800/60 ${active ? "bg-emerald-50 dark:bg-emerald-500/10" : "hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}`}
              >
                <div className="relative">
                  {c.picture ? <img src={c.picture} className="h-11 w-11 rounded-full object-cover" alt={c.name} /> :
                    <div className="grid h-11 w-11 place-items-center rounded-full bg-emerald-500 text-sm font-semibold text-white">{c.name?.[0]?.toUpperCase()}</div>}
                  {online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 dark:border-zinc-900"/>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{c.name}</span>
                      <TierBadge tier={c.tier} size="xs" />
                    </div>
                    {c.unread > 0 && <span className="ml-2 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{c.unread}</span>}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    {c.last_message
                      ? `${c.last_message.from_me ? "You: " : ""}${c.last_message.content}`
                      : "Say hi 👋"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Thread */}
      <section className={`flex min-h-0 flex-col ${activeFriendId ? "flex" : "hidden md:flex"}`}>
        {!activeFriendId ? (
          <div className="flex flex-1 items-center justify-center p-10 text-center text-zinc-500">
            <div>
              <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-600"><Send size={20}/></div>
              <p className="text-sm">Pick a friend to start chatting.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
              {/* On mobile the ← arrow goes back to the conversation list;
                  on desktop, where the sidebar is always visible, it goes
                  straight to Community. This preserves both browser back
                  behavior (via history stack) and the WhatsApp-style
                  in-app return path. */}
              <button
                className="md:hidden -ml-1 rounded-md p-1 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                onClick={() => navigate("/messages")}
                aria-label="Back to messages"
                data-testid="back-to-list-btn"
              >
                <ArrowLeft size={18}/>
              </button>
              <button
                className="hidden md:inline-flex -ml-1 items-center gap-1 rounded-md p-1 pr-2 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                onClick={() => navigate("/community")}
                aria-label="Back to Community"
                data-testid="back-to-community-btn"
              >
                <ArrowLeft size={16}/> Community
              </button>
              {friend?.picture ? <img src={friend.picture} className="h-9 w-9 rounded-full object-cover" alt={friend.name}/> :
                <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">{friend?.name?.[0]?.toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="truncate text-sm font-medium" data-testid="chat-friend-name">{friend?.name || "Loading…"}</div>
                  <TierBadge tier={friend?.tier} size="xs" />
                </div>
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                  {isOnline(friend?.last_seen) ? (
                    <><Circle size={8} fill="#10B981" stroke="#10B981"/> <span>Online</span></>
                  ) : (
                    <span>Last seen {timeAgo(friend?.last_seen)}</span>
                  )}
                </div>
              </div>
              {/* Delete Chat — confirmation modal-gated hard delete. Disabled
                  while the friend hasn't loaded so we don't accidentally
                  delete a stale conversation reference. */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-zinc-500 hover:text-red-600 dark:hover:text-red-400"
                    aria-label="Delete chat"
                    data-testid="delete-chat-btn"
                    disabled={!friend}
                  >
                    <Trash2 size={16}/>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-chat-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
                    <AlertDialogDescription>
                      All messages between you and <span className="font-medium">{friend?.name}</span> will be permanently removed. You can still send new messages later — this only clears the history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-chat-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={deleteChat}
                      data-testid="delete-chat-confirm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </header>

            <div
              ref={listRef}
              onScroll={handleListScroll}
              className="flex-1 space-y-2 overflow-y-auto overscroll-contain bg-zinc-50 p-4 scrollbar-thin dark:bg-zinc-950/40"
              data-testid="message-list"
            >
              {messages.length === 0 && <div className="py-12 text-center text-sm text-zinc-500">No messages yet. Say hi 👋</div>}
              {messages.map((m) => {
                const mine = m.from_user_id === currentUser.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "rounded-br-sm bg-emerald-500 text-white" : "rounded-bl-sm bg-white text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100"}`}>
                      <div className="whitespace-pre-wrap break-words">{m.content}</div>
                      <div className={`mt-1 text-[10px] ${mine ? "text-emerald-50/80" : "text-zinc-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 p-3 dark:border-zinc-800">
              <Input value={draft} onChange={(e)=>setDraft(e.target.value)} placeholder="Type a message…" data-testid="message-input" autoFocus/>
              <Button type="submit" disabled={!draft.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="send-message-btn"><Send size={16}/></Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
